import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Check } from 'lucide-react';
import { PASSWORD_HINT, passwordProblem } from '@trellis/shared';
import { ApiError, api } from '../lib/api';
import { Button, Card, Field, Input, PasswordInput } from '../components/ui';

/**
 * Donde aterriza el enlace de "olvidé mi contraseña".
 *
 * Esta pantalla faltaba. El correo de recuperación se venía mandando con un
 * enlace a `/nueva-clave?token=…` desde siempre, y esa dirección no estaba
 * declarada en ninguna ruta: quien la abría veía la pantalla de ingreso, sin
 * ningún aviso, y el token se perdía. Así que aunque el mail hubiera llegado
 * —que es lo que veníamos persiguiendo— el recupero igual no terminaba.
 *
 * No pide la contraseña actual, a diferencia del cambio voluntario: quien llega
 * acá justamente no la tiene. Lo que autoriza el cambio es el token del enlace,
 * que vive dos horas y se quema al usarlo.
 */
export function NuevaClave() {
  const { slug } = useParams<{ slug: string }>();
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const token = params.get('token');

  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const problema = next.length > 0 ? passwordProblem(next) : null;
  const mismatch = confirm.length > 0 && next !== confirm;
  const destino = slug ? `/${slug}` : '/ingresar';

  // Sin token no hay nada que hacer acá, y hay que decirlo: el enlace vencido
  // es el caso más común, y una pantalla en blanco deja pensando que la app
  // se rompió en vez de que hay que pedir uno nuevo.
  if (!token) {
    return (
      <div className="min-h-screen grid place-items-center px-5 py-10 bg-surface">
        <div className="w-full max-w-sm text-center">
          <h1 className="text-2xl font-semibold tracking-tight">Ese enlace no sirve</h1>
          <p className="text-fg-muted mt-2 text-[15px] leading-relaxed">
            Puede que esté incompleto o que ya lo hayas usado. Pedí uno nuevo desde "Olvidé mi
            contraseña" y entrá al más reciente.
          </p>
          <Button className="mt-6" size="lg" fullWidth onClick={() => navigate(destino)}>
            Ir a ingresar
          </Button>
        </div>
      </div>
    );
  }

  const handleSubmit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    setError(null);

    const falta = passwordProblem(next);
    if (falta) {
      setError(falta);
      return;
    }
    if (next !== confirm) {
      setError('Las contraseñas no coinciden');
      return;
    }

    setSubmitting(true);
    try {
      await api.post('/auth/reset-password', { token, password: next });
      setDone(true);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'No pudimos cambiar tu contraseña');
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen grid place-items-center px-5 py-10 bg-surface">
        <div className="w-full max-w-sm text-center">
          <div className="size-14 rounded-full bg-success-soft text-success grid place-items-center mx-auto">
            <Check className="size-7" />
          </div>
          <h1 className="text-2xl font-semibold tracking-tight mt-5">Listo, ya está cambiada</h1>
          <p className="text-fg-muted mt-2 text-[15px] leading-relaxed">
            Entrá con tu contraseña nueva. Si tenías la sesión abierta en otro dispositivo, ahí vas
            a tener que entrar de nuevo.
          </p>
          <Button
            className="mt-6"
            size="lg"
            fullWidth
            onClick={() => navigate(destino, { replace: true })}
          >
            Ingresar
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid place-items-center px-5 py-10 bg-surface">
      <div className="w-full max-w-sm">
        <h1 className="text-2xl font-semibold tracking-tight text-center">Creá tu contraseña</h1>
        <p className="text-fg-muted text-center mt-2 text-[15px]">
          Elegí una contraseña nueva para entrar.
        </p>

        <Card className="p-6 mt-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <Field
              label="Contraseña nueva"
              htmlFor="nueva"
              hint={PASSWORD_HINT}
              error={problema ?? undefined}
            >
              <PasswordInput
                id="nueva"
                autoComplete="new-password"
                value={next}
                onChange={(event) => setNext(event.target.value)}
                required
              />
            </Field>

            <Field
              label="Repetí la contraseña nueva"
              htmlFor="repetir"
              error={mismatch ? 'No coinciden' : undefined}
            >
              <PasswordInput
                id="repetir"
                autoComplete="new-password"
                value={confirm}
                onChange={(event) => setConfirm(event.target.value)}
                required
              />
            </Field>

            {error && (
              <p className="text-sm text-danger" role="alert">
                {error}
              </p>
            )}

            <Button
              type="submit"
              loading={submitting}
              disabled={Boolean(problema) || mismatch || !next}
              fullWidth
              size="lg"
            >
              Guardar contraseña
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
}
